import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip,
  TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Stack, Tooltip, IconButton,
} from '@mui/material';
import { Block, LockOpen, Refresh, Add } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

// Blocked Numbers — DIDs taken away from every customer. A blocked number is offline (inbound calls get
// "no route"), cannot be assigned, and is never used as an outbound caller-ID. Unblocking returns it to
// the available inventory; it must then be assigned explicitly.
export const BlockedNumbersPage: React.FC = () => {
  const { user } = useAuthStore();
  const canWrite = ['super_admin', 'provider_admin', 'technical_admin'].includes(user?.role || '');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<{ sev: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockText, setBlockText] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/numbers/blocked' + (search ? `?search=${encodeURIComponent(search)}` : ''));
      setRows(data || []); setSel({});
    } catch (e: any) { setMsg({ sev: 'error', text: e.response?.data?.message || 'Failed to load blocked numbers' }); }
    finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const selIds = () => Object.keys(sel).filter(k => sel[k]);
  const allSelected = rows.length > 0 && rows.every(r => sel[r.id]);

  const unblock = async (ids: string[]) => {
    if (!ids.length) return;
    if (!window.confirm(`Unblock ${ids.length} number(s)? They return to the available inventory (not assigned to anyone).`)) return;
    setBusy(true);
    try { const { data } = await api.post('/numbers/bulk/unblock', { ids }); setMsg({ sev: 'success', text: data.message }); load(); }
    catch (e: any) { setMsg({ sev: 'error', text: e.response?.data?.message || 'Unblock failed' }); }
    finally { setBusy(false); }
  };

  const blockNumbers = async () => {
    const numbers = blockText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (!numbers.length) return;
    setBusy(true);
    try {
      const { data } = await api.post('/numbers/bulk/block', { numbers, reason: blockReason || undefined });
      setMsg({ sev: 'success', text: data.message }); setBlockDialog(false); setBlockText(''); setBlockReason(''); load();
    } catch (e: any) { setMsg({ sev: 'error', text: e.response?.data?.message || 'Block failed' }); }
    finally { setBusy(false); }
  };

  const byReason: Record<string, number> = {};
  rows.forEach(r => { const k = r.blocked_from || '—'; byReason[k] = (byReason[k] || 0) + 1; });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}><Block sx={{ verticalAlign: 'middle', mr: 1 }} />Blocked Numbers</Typography>
          <Typography variant="body2" color="text.secondary">
            Numbers removed from all customers. Blocked numbers are offline (inbound gets no route), cannot be assigned, and are never presented as caller-ID.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Search number" value={search} onChange={e => setSearch(e.target.value)} />
          <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={load}>Refresh</Button>
          {canWrite && <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setBlockDialog(true)}>Block numbers</Button>}
          {canWrite && <Button startIcon={<LockOpen />} variant="outlined" color="warning" size="small" disabled={!selIds().length || busy} onClick={() => unblock(selIds())}>Unblock selected ({selIds().length})</Button>}
        </Stack>
      </Box>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${rows.length} blocked`} color="error" />
        {Object.entries(byReason).sort((a, b) => b[1] - a[1]).map(([k, v]) => <Chip key={k} label={`from ${k}: ${v}`} variant="outlined" size="small" />)}
      </Stack>

      <Card><CardContent>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell padding="checkbox">{canWrite && <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selIds().length > 0}
              onChange={e => { const n: Record<string, boolean> = {}; if (e.target.checked) rows.forEach(r => { n[r.id] = true; }); setSel(n); }} />}</TableCell>
            {['Number', 'Country', 'Carrier', 'Ordered for', 'Taken from', 'Reason', 'Blocked on', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell padding="checkbox">{canWrite && <Checkbox size="small" checked={!!sel[r.id]} onChange={e => setSel({ ...sel, [r.id]: e.target.checked })} />}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.number}</TableCell>
                <TableCell>{r.country_code || '—'}</TableCell>
                <TableCell>{r.carrier_name || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.ordered_for || '—'}{r.order_ref ? <span style={{ color: '#999' }}> ({r.order_ref})</span> : ''}</TableCell>
                <TableCell>{r.blocked_from || '—'}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 12, maxWidth: 360 }}>{r.blocked_reason || '—'}</TableCell>
                <TableCell>{r.blocked_at ? new Date(r.blocked_at).toLocaleString() : '—'}</TableCell>
                <TableCell align="right">{canWrite && <Tooltip title="Unblock (return to inventory)"><span><IconButton size="small" disabled={busy} onClick={() => unblock([r.id])}><LockOpen fontSize="small" /></IconButton></span></Tooltip>}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No blocked numbers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={blockDialog} onClose={() => setBlockDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block numbers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste numbers (one per line, E.164 with country code). Assigned numbers are taken away from their customer immediately; numbers not yet in inventory are added as blocked.
          </Typography>
          <TextField fullWidth multiline minRows={6} label="Numbers" placeholder={'+442046131900\n+442046131901'} value={blockText} onChange={e => setBlockText(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Reason (optional)" value={blockReason} onChange={e => setBlockReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<Block />} disabled={busy || !blockText.trim()} onClick={blockNumbers}>Block</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
